import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';

// Define the stats interface
interface ConnectionStats {
  resolution: string;
  fps: number;
  currentRoundTripTime: number;
  availableOutgoingBitrate: number;
  packetsReceived: number;
  packetsLost: number;
}

// Simulated WebRTC Enterprise Demo (with native device support when available)
const WebRTCEnterpriseDemo: React.FC = () => {
  const [connectionStatus, setConnectionStatus] = useState('Initializing...');
  const [isConnected, setIsConnected] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [isNativeEnvironment, setIsNativeEnvironment] = useState(false);
  const [hasCamera, setHasCamera] = useState(false);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const [stats, setStats] = useState<ConnectionStats>({
    resolution: '1280x720',
    fps: 30,
    currentRoundTripTime: 45,
    availableOutgoingBitrate: 2500,
    packetsReceived: 1254,
    packetsLost: 2
  });
  
  // Add log entry
  const addLog = (message: string) => {
    console.log(message);
    setLogs(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };
  
  // Check if we're in a native environment with device access
  useEffect(() => {
    const checkEnvironment = async () => {
      try {
        // Check if navigator.mediaDevices is available
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
          addLog('Native device access detected');
          setIsNativeEnvironment(true);
          
          // Check if camera is available
          try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
            setHasCamera(true);
            addLog('Camera access granted');
            
            // Display the camera feed if we have a video element
            if (localVideoRef.current) {
              localVideoRef.current.srcObject = stream;
            }
            
            // We'll keep the stream for later use
            return () => {
              stream.getTracks().forEach(track => track.stop());
            };
          } catch (err) {
            addLog(`Camera access error: ${err instanceof Error ? err.message : 'Unknown error'}`);
            setHasCamera(false);
          }
        } else {
          addLog('Running in WSL or environment without device access');
          setIsNativeEnvironment(false);
        }
      } catch (error) {
        addLog(`Environment check error: ${error instanceof Error ? error.message : 'Unknown error'}`);
        setIsNativeEnvironment(false);
      }
    };
    
    checkEnvironment();
  }, []);
  
  // Simulate WebRTC connection process
  useEffect(() => {
    // Simulate connection process
    const simulateConnection = async () => {
      addLog('Starting WebRTC initialization...');
      setConnectionStatus('Checking environment...');
      await sleep(800);
      
      addLog('Environment check passed');
      setConnectionStatus('Requesting media permissions...');
      await sleep(1200);
      
      addLog('Media permissions granted (simulated)');
      setConnectionStatus('Creating peer connection...');
      await sleep(1000);
      
      addLog('Peer connection created');
      setConnectionStatus('Gathering ICE candidates...');
      await sleep(1500);
      
      addLog('ICE candidate: candidate:1 1 UDP 2122252543 192.168.1.100 49823 typ host');
      addLog('ICE candidate: candidate:2 1 TCP 1518280447 192.168.1.100 9 typ host tcptype active');
      await sleep(800);
      
      addLog('Creating SDP offer...');
      setConnectionStatus('Creating offer...');
      await sleep(1000);
      
      addLog('SDP offer created');
      setConnectionStatus('Waiting for answer...');
      await sleep(1200);
      
      addLog('SDP answer received');
      setConnectionStatus('Establishing connection...');
      await sleep(1500);
      
      addLog('Connection established');
      setConnectionStatus('Connected');
      setIsConnected(true);
      
      // Start updating stats periodically
      const statsInterval = setInterval(() => {
        setStats((prev: ConnectionStats) => ({
          resolution: prev.resolution,
          fps: Math.max(25, Math.min(60, prev.fps + Math.floor(Math.random() * 7) - 3)),
          currentRoundTripTime: Math.max(20, Math.min(150, prev.currentRoundTripTime + Math.floor(Math.random() * 11) - 5)),
          availableOutgoingBitrate: Math.max(1000, Math.min(5000, prev.availableOutgoingBitrate + Math.floor(Math.random() * 200) - 100)),
          packetsReceived: prev.packetsReceived + Math.floor(Math.random() * 50) + 10,
          packetsLost: prev.packetsLost + (Math.random() > 0.8 ? 1 : 0)
        }));
      }, 2000);
      
      return () => clearInterval(statsInterval);
    };
    
    simulateConnection();
  }, []);
  
  // Helper function for async sleep
  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
  
  // Toggle screen sharing (with native support when available)
  const toggleScreenShare = async () => {
    if (!isScreenSharing) {
      addLog('Requesting screen sharing permission...');
      
      if (isNativeEnvironment && navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
        try {
          // Use actual screen sharing in native environment
          const screenStream = await navigator.mediaDevices.getDisplayMedia({ 
            video: true,
            audio: false
          });
          
          addLog('Screen sharing started (native)');
          setIsScreenSharing(true);
          
          // Display the screen sharing feed if we have a video element
          if (localVideoRef.current) {
            localVideoRef.current.srcObject = screenStream;
          }
          
          // Update stats to reflect screen sharing
          setStats(prev => ({
            ...prev,
            resolution: '1920x1080',
            fps: 15
          }));
          
          // Set up a listener for when the user stops sharing via the browser UI
          screenStream.getVideoTracks()[0].onended = () => {
            addLog('Screen sharing stopped (by browser)');
            setIsScreenSharing(false);
            
            // Restore camera feed if available
            if (hasCamera) {
              navigator.mediaDevices.getUserMedia({ video: true, audio: true })
                .then(stream => {
                  if (localVideoRef.current) {
                    localVideoRef.current.srcObject = stream;
                  }
                })
                .catch(err => addLog(`Error restoring camera: ${err instanceof Error ? err.message : 'Unknown error'}`));
            }
            
            // Restore original stats
            setStats(prev => ({
              ...prev,
              resolution: '1280x720',
              fps: 30
            }));
          };
        } catch (err) {
          // User denied permission or other error
          addLog(`Screen sharing error: ${err instanceof Error ? err.message : 'Unknown error'}`);
          // Fall back to simulation
          simulateScreenSharing();
        }
      } else {
        // Fall back to simulation in WSL
        await sleep(1000);
        simulateScreenSharing();
      }
    } else {
      // Stop screen sharing
      if (isNativeEnvironment && localVideoRef.current && localVideoRef.current.srcObject) {
        // Stop all tracks
        const stream = localVideoRef.current.srcObject as MediaStream;
        stream.getTracks().forEach(track => track.stop());
        
        // Restore camera if available
        if (hasCamera) {
          try {
            const cameraStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
            if (localVideoRef.current) {
              localVideoRef.current.srcObject = cameraStream;
            }
          } catch (err) {
            addLog(`Error restoring camera: ${err instanceof Error ? err.message : 'Unknown error'}`);
          }
        }
      }
      
      addLog('Screen sharing stopped');
      setIsScreenSharing(false);
      
      // Restore original stats
      setStats(prev => ({
        ...prev,
        resolution: '1280x720',
        fps: 30
      }));
    }
  };
  
  // Simulate screen sharing for WSL environment
  const simulateScreenSharing = () => {
    addLog('Screen sharing started (simulated)');
    setIsScreenSharing(true);
    
    // Update stats to reflect screen sharing
    setStats(prev => ({
      ...prev,
      resolution: '1920x1080',
      fps: 15
    }));
  };
  
  // Send test message (simulated)
  const sendMessage = async () => {
    const message = `Test message: ${new Date().toLocaleTimeString()}`;
    addLog(`Sending message: ${message}`);
    await sleep(300);
    addLog(`Message sent successfully`);
    
    // Simulate receiving a response
    await sleep(800);
    addLog(`Received response: Message received at ${new Date().toLocaleTimeString()}`);
  };
  
  // Render different content based on screen sharing state
  const renderLocalStream = () => {
    // If we have native camera access, show the video element
    if (isNativeEnvironment) {
      return (
        <div className="native-video-container">
          <video 
            ref={localVideoRef} 
            autoPlay 
            playsInline 
            muted 
            className="native-video-element"
          />
          {isScreenSharing && (
            <div className="screen-sharing-badge-overlay">SCREEN SHARING</div>
          )}
        </div>
      );
    }
    
    // Otherwise, show the simulated view
    if (isScreenSharing) {
      return (
        <div className="screen-sharing-view">
          <div className="screen-sharing-header">
            <span className="screen-title">Presentation.pptx</span>
            <span className="screen-sharing-badge">SCREEN SHARING</span>
          </div>
          <div className="screen-sharing-content">
            <h2>WebRTC Enterprise Features</h2>
            <ul>
              <li>Secure P2P Communication</li>
              <li>Low Latency Video Conferencing</li>
              <li>Real-time Data Channels</li>
              <li>Adaptive Streaming Quality</li>
            </ul>
          </div>
        </div>
      );
    } else {
      return (
        <div className="video-stream-view">
          <div className="placeholder-icon">📹</div>
          <div className="placeholder-text">Local Video Stream</div>
          {isNativeEnvironment && !hasCamera && (
            <div className="camera-error">Camera not available</div>
          )}
        </div>
      );
    }
  };
  
  return (
    <div className="webrtc-demo">
      <header>
        <h1>WebRTC Enterprise Collaboration Platform</h1>
        <div className="connection-status">
          <span className={isConnected ? 'connected' : 'disconnected'}>
            {connectionStatus}
          </span>
        </div>
      </header>
      
      <div className="main-content">
        <div className="video-container">
          <div className="video-wrapper">
            <div className={`video-placeholder ${isScreenSharing ? 'screen-sharing' : ''}`}>
              {renderLocalStream()}
            </div>
            <div className="video-label">Local Stream {isScreenSharing && '(Screen Sharing)'}</div>
          </div>
          
          <div className="video-wrapper">
            <div className="video-placeholder">
              {isScreenSharing ? (
                <div className="remote-viewing-screen">
                  <div className="remote-viewing-icon">👁️</div>
                  <div className="remote-viewing-text">Viewing your shared screen</div>
                </div>
              ) : (
                <div className="video-stream-view">
                  <div className="placeholder-icon">👥</div>
                  <div className="placeholder-text">Remote Video Stream</div>
                </div>
              )}
            </div>
            <div className="video-label">Remote Stream</div>
          </div>
        </div>
        
        <div className="controls">
          <button 
            onClick={toggleScreenShare}
            className={isScreenSharing ? 'active' : ''}
          >
            {isScreenSharing ? 'Stop Sharing' : 'Share Screen'}
          </button>
          
          <button onClick={sendMessage} disabled={!isConnected}>
            Send Test Message
          </button>
        </div>
        
        <div className="stats-panel">
          <h3>Connection Statistics</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-label">Resolution:</span>
              <span className="stat-value">{stats.resolution}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Frame Rate:</span>
              <span className="stat-value">{stats.fps} fps</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Round Trip Time:</span>
              <span className="stat-value">{stats.currentRoundTripTime} ms</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Available Bitrate:</span>
              <span className="stat-value">{stats.availableOutgoingBitrate} kbps</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Packets Received:</span>
              <span className="stat-value">{stats.packetsReceived}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Packets Lost:</span>
              <span className="stat-value">{stats.packetsLost}</span>
            </div>
          </div>
        </div>
        
        <div className="log-panel">
          <h3>Connection Log</h3>
          <div className="log-container">
            {logs.map((log, index) => (
              <div key={index} className="log-entry">{log}</div>
            ))}
          </div>
        </div>
      </div>
      
      {isNativeEnvironment && (
        <div className="environment-badge">
          Native Environment Detected
        </div>
      )}
    </div>
  );
};

// Create root and render app
const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <WebRTCEnterpriseDemo />
  </React.StrictMode>
); 